import { createSelector, createSlice } from "@reduxjs/toolkit";
import { sortDataBy } from "../../../SharedModule/utils/dataSort";
import { createChangeOrderReducer } from "../../../SharedModule/utils/changeOrderReducer";
import {
  UserWithProfileType,
  SupervisorFilter,
  CostCenterDropdown,
} from "../../types/users-module-types";
import { getSupervisorName } from "../../utils/userUtils";
import { addElementToArray } from "../../../TimesModule/utils/timesUtils";
import { STATUS_TERMINATED } from "../../../SharedModule/utils/constants";
import { CostCenterType } from "../../../TimesModule/types/times-module-types";

type UsersState = {
  userlist: UserWithProfileType[];
  costCenters: CostCenterDropdown[];
  costCenterUsersComplete: CostCenterType[] | [];
  costCentersOwned: CostCenterType[] | null;
  costCentersOwnedIds: number[] | null;
  orderedBy: {
    orderBy: string;
    criteria: "asc" | "desc";
    dataType?: "string";
  };
  isShowingActives: boolean;
  filterUsers: {
    searchText: string;
    costCenters: CostCenterDropdown[];
    supervisors: SupervisorFilter[];
  };
  isLoadingUsers: boolean;
  isLoadingUserProfile: boolean;
  actualUser: UserWithProfileType | null;
  actualUserEmail: string | null;
  actualUserRoles: any[] | null;
  supervisors: SupervisorFilter[];
};

const initialState: UsersState = {
  userlist: [],
  costCenters: [],
  costCenterUsersComplete: [],
  costCentersOwned: null,
  costCentersOwnedIds: null,
  orderedBy: {
    orderBy: "fullName",
    criteria: "asc",
    dataType: "string",
  },
  isShowingActives: true,
  filterUsers: {
    searchText: "",
    costCenters: [],
    supervisors: [],
  },
  isLoadingUsers: true,
  isLoadingUserProfile: true,
  actualUser: null,
  actualUserEmail: null,
  actualUserRoles: null,
  supervisors: [],
};

export const usersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    load: (state, action) => {
      state.costCentersOwnedIds = [];
      state.costCenters = [];
      state.costCenterUsersComplete = action.payload.costCentersInfo;
      state.costCentersOwned = action.payload.useAllCostCenters
        ? []
        : action.payload.costCentersOwned;
      addElementToArray(
        state.costCenters,
        action.payload.useAllCostCenters
          ? action.payload.costCentersInfo
          : action.payload.costCentersOwned
      );
      if (action.payload.costCentersOwned) {
        state.costCentersOwnedIds = state.costCenters.map((elem) => elem.value);
        state.userlist = action.payload.profileInfo.filter(
          (user) =>
            user.costCenter &&
            state.costCentersOwnedIds?.includes(user.costCenter.number)
        );
      } else {
        state.userlist = action.payload.profileInfo;
      }
      state.userlist.forEach((user) => {
        if (user.userProfile?.supervisorId) {
          user.userProfile.supervisorName = getSupervisorName(
            action.payload.allUsers,
            user.userProfile?.supervisorId
          );
        }
      });
      state.supervisors = [];
      // if endpoint of supervisor get simple, this logic will be remove
      action.payload.supervisors.forEach((elem) => {
        state.supervisors.push({
          value: elem.id,
          label: elem.fullName,
          email: elem.email,
          isActive: elem.isActive,
        });
      });
      state.isLoadingUsers = false;
    },
    setIsLoadingUsers: (state, action) => {
      state.isLoadingUsers = action.payload;
    },
    setIsLoadingUserProfile: (state, action) => {
      state.isLoadingUserProfile = action.payload;
    },
    setShowActives: (state, action) => {
      state.isShowingActives = action.payload;
    },
    setFiltersUsers: (state, action) => {
      state.filterUsers = { ...state.filterUsers, ...action.payload };
    },
    resetFiltersUsers: (state) => {
      return { ...state, filterUsers: { ...initialState.filterUsers } };
    },
    setActualUser: (state, action) => {
      state.actualUser = action.payload.actualUserData;
      if (state.actualUser?.userProfile && action.payload.loadSupervisorName) {
        state.actualUser.userProfile.supervisorName = getSupervisorName(
          action.payload.allUsers,
          state.actualUser.userProfile.supervisorId
        );
      }
    },
    setActualUserEmail: (state, action) => {
      state.actualUserEmail = action.payload;
    },
    setActualUserSettings: (state, action) => {
      if (state.actualUser?.userProfile) {
        state.actualUser.userProfile.efficiencyBonus =
          action.payload.efficiencyBonus;
        state.actualUser.userProfile.entryReminder =
          action.payload.entryReminder;
      }
      state.userlist = state.userlist.map((user: any) =>
        user.email === action.payload.email
          ? {
              ...user,
              userProfile: {
                ...user.userProfile,
                entryReminder: action.payload.entryReminder,
                efficiencyBonus: action.payload.efficiencyBonus,
              },
            }
          : user
      );
    },
    setActualUserRoles: (state, action) => {
      state.actualUserRoles = action.payload;
      state.isLoadingUserProfile = false;
    },
    setCostCentersOwned: (state, action) => {
      state.costCentersOwned = action.payload;
    },
    setCostCentersOwnedIds: (state, action) => {
      state.costCentersOwnedIds = action.payload;
    },
    changeOrder: createChangeOrderReducer("orderedBy"),
  },
});

// Action creators are generated for each case reducer function
export const {
  load,
  setIsLoadingUsers,
  setIsLoadingUserProfile,
  setShowActives,
  setFiltersUsers,
  resetFiltersUsers,
  setActualUser,
  setActualUserEmail,
  setActualUserSettings,
  setActualUserRoles,
  setCostCentersOwned,
  setCostCentersOwnedIds,
  changeOrder,
} = usersSlice.actions;

export const IsLoadingUsers = (state: any) => {
  return state.users.isLoadingUsers;
};

export const IsLoadingUserProfile = (state: any) => {
  return state.users.isLoadingUserProfile;
};

export const SelectUsers = ({ users }: { users: UsersState }) => {
  return { userlist: users.userlist };
};

export const SelectFiltersUsers = (state: any) => {
  return state.users.filterUsers;
};

export const SelectUsersFiltered = (state: any) => {
  return state.users.userlist
    .filter(
      (user) =>
        user.isActive &&
        user.userProfile?.employmentStatus !== STATUS_TERMINATED
    )
    .map((user) => ({ label: user.fullName, value: user.id }));
};

export const SelectSupervisors = ({ users }: { users: UsersState }) => {
  return users.supervisors;
};

export const SelectCostCenters = (state: any) => {
  return state.users.costCenters;
};

export const SelectCostCentersOwned = (state: any) => {
  return state.users.costCentersOwned;
};

export const SelectCostCentersOwnedIds = (state: any) => {
  return state.users.costCentersOwnedIds;
};

export const SelectCostCentersUsersComplete = (state: any) => {
  return state.users.costCenterUsersComplete;
};

export const SelectActualUser = (state: any) => {
  return state.users.actualUser;
};

export const SelectActualUserEmail = (state: any) => {
  return state.users.actualUserEmail;
};

export const SelectActualUserRoles = (state: any) => {
  return state.users.actualUserRoles;
};

export const IsShowingActives = (state: any) => {
  return state.users.isShowingActives;
};

export const SelectOrderCriteria = ({ users }: { users: UsersState }) =>
  users.orderedBy;

export const SelectOrderedBy = createSelector(
  [SelectUsers, SelectOrderCriteria],
  (user, orderCriteria) => {
    return {
      userlist:
        (user.userlist &&
          sortDataBy(
            user.userlist,
            orderCriteria.orderBy,
            orderCriteria.criteria,
            orderCriteria.dataType
          )) ||
        [],
    };
  }
);

export default usersSlice.reducer;
